import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Mail, Phone, Calendar } from 'lucide-react';

const ContactForm = () => {
  const [result, setResult] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult("Please wait...");

    const formData = new FormData(e.target);
    const object = {};
    formData.forEach((value, key) => {
      object[key] = value;
    });

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(object)
      });

      const json = await response.json();
      
      if (response.status === 200) {
        setResult(json.message || "Message sent successfully!");
        e.target.reset();
        setTimeout(() => {
          setResult("");
        }, 5000);
      } else {
        setResult(json.message || "Something went wrong!");
      }
    } catch (error) {
      console.log(error);
      setResult("Something went wrong!");
    }
  };

  return (
    <section id="contact-section" className="py-24 bg-gradient-to-br from-[#6A7EC9] to-[#33C7A9] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Let's Build Something Together
            </h2>
            <p className="text-xl text-white/90">
              Ready to transform your location data into actionable insights?
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Web3Forms Contact Form */}
            <Card className="md:col-span-2 p-8 bg-white/95 backdrop-blur-sm shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="hidden" name="access_key" value="e3a3488e-d67a-4de6-b6de-018e005c1cb1" />
                <input type="hidden" name="subject" value="New Contact from LiftGeo Website" />
                <input type="checkbox" name="botcheck" style={{ display: 'none' }} />

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="first_name" className="block mb-2 text-sm font-semibold text-gray-700">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="first_name"
                      placeholder="John"
                      required
                      className="w-full px-4 py-3 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#33C7A9] focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block mb-2 text-sm font-semibold text-gray-700">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      id="last_name"
                      placeholder="Doe"
                      required
                      className="w-full px-4 py-3 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#33C7A9] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block mb-2 text-sm font-semibold text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      placeholder="you@company.com"
                      required
                      className="w-full px-4 py-3 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#33C7A9] focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block mb-2 text-sm font-semibold text-gray-700">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      placeholder="+1 (555) 123-4567"
                      required
                      className="w-full px-4 py-3 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#33C7A9] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block mb-2 text-sm font-semibold text-gray-700">
                    Your Message *
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows="5"
                    placeholder="Tell us about your project and how we can help..."
                    required
                    className="w-full px-4 py-3 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#33C7A9] focus:border-transparent resize-none transition-all"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-4 text-white font-bold text-lg bg-[#33C7A9] hover:bg-[#2AB392] rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Send Message
                </button>

                {result && (
                  <p className={`text-center text-base ${result.includes('success') || result.includes('sent') ? 'text-green-600' : result.includes('wait') ? 'text-gray-600' : 'text-red-600'}`}>
                    {result}
                  </p>
                )}
              </form>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="p-6 bg-white/95 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#33C7A9]/10 rounded-lg">
                    <Mail className="h-6 w-6 text-[#33C7A9]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Email Us</h3>
                    <a href="mailto:hello@liftgeo.com" className="text-gray-600 hover:text-[#33C7A9] transition-colors">
                      hello@liftgeo.com
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white/95 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#6A7EC9]/10 rounded-lg">
                    <Phone className="h-6 w-6 text-[#6A7EC9]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Call Us</h3>
                    <a href="tel:+15551234567" className="text-gray-600 hover:text-[#6A7EC9] transition-colors">
                      (555) 123-4567
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white/95 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <a 
                  href="https://calendly.com/christen-erichsen/30min" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <div className="p-3 bg-[#6A7EC9]/10 rounded-lg group-hover:bg-[#6A7EC9]/20 transition-colors">
                    <Calendar className="h-6 w-6 text-[#6A7EC9]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#6A7EC9] transition-colors">
                      Schedule a 15 min chat
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Book a time that works for you
                    </p>
                  </div>
                </a>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;